export const settingsEn = {
  title: "Settings",
  profile: {
    title: "Profile",
    description: "Update your name and sign-in email.",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    currentEmail: "Current email confirmation",
    currentEmailHint: "Required only when changing email.",
    currentEmailMismatch: "Current email does not match your account email.",
    emailInUse: "That email is already in use.",
  },
  unsavedDialog: {
    title: "Unsaved changes",
    description: "You have unsaved settings. Save before leaving this page?",
    saveAndLeave: "Save and leave",
    discardAndLeave: "Discard and leave",
    stay: "Stay",
  },
  bodyDiagram: {
    title: "Body diagram",
    description: "Choose the paper doll style used in your check-in body diagram.",
    selectionLabel: "Paper doll selection",
    placeholder: "Choose a body model",
    masculine: "Masculine",
    feminine: "Feminine",
  },
  language: {
    title: "Language",
    description: "Choose the language used in the application interface.",
    label: "Application language",
    placeholder: "Choose a language",
    english: "English",
    finnish: "Suomi",
  },
  aiCheckInLanguage: {
    title: "AI check-in language",
    description: "Choose the language used for voice transcription in AI check-ins.",
    label: "AI check-in transcription language",
    placeholder: "Choose a language",
    appDefault: "Use application language",
    english: "English",
    finnish: "Suomi",
  },
  dateFormat: {
    title: "Date format",
    description: "Choose how dates are displayed across the app.",
    label: "Preferred date format",
    placeholder: "Choose a format",
    locale: "Use language default",
    dmy: "DD/MM/YYYY",
    mdy: "MM/DD/YYYY",
    ymd: "YYYY-MM-DD",
  },
  weather: {
    title: "Weather",
    description: "Add a default location to see weather data overlaid on your pain trends.",
    locationLabel: "Default location",
    locationPlaceholder: "Start typing a city...",
    searchButton: "Search",
    searchHint: "Select a city from the suggestion list.",
    searching: "Searching cities...",
    noMatches: "No city matches that search.",
    selectionRequired: "Pick a city from the suggestion list before adding.",
    noLocations: "No locations added yet. Search for a city above.",
    defaultBadge: "Default",
    setDefault: "Set as default",
    deleteLocation: "Remove",
    deleteConfirmation: "Remove this location? Weather history for it will be kept.",
    deleteConfirmTitle: "Remove location",
    temperatureUnitLabel: "Temperature unit",
    celsius: "Celsius (°C)",
    fahrenheit: "Fahrenheit (°F)",
    overlayToggle: "Show weather on trends chart",
    privacyNote: "We store your selected city and its coordinates for weather and timezone context. We do not track your device GPS or movements.",
    searchError: "Could not find that city. Please try a different search.",
    rateLimitNote: "Too many requests — please wait a moment.",
  },
  consent: {
    title: "Data & privacy consents",
    description:
      "Manage the consents that control how Floline processes your health data.",
    saveError: "Could not save consent change. Please try again.",
    saved: "Consent updated.",
    granted: "Granted",
    withdrawn: "Withdrawn",
    grantAction: "Grant consent",
    withdrawConfirmTitle: "Withdraw consent",
    withdrawConfirmDescription:
      "Are you sure you want to withdraw consent for {{label}}? Features that depend on it will be disabled.",
    withdrawConfirmAction: "Withdraw consent",
    showHistory: "Show consent history",
    hideHistory: "Hide consent history",
    historyTitle: "Consent history",
    historyEmpty: "No consent events recorded yet.",
    historyVersion: "v{{version}}",
    historyAction_give: "Granted",
    historyAction_withdraw: "Withdrawn",
    coreHealthProcessing: {
      label: "Core health-data processing",
      text: "I consent to Floline storing and processing the pain, symptom, and well-being data I enter so the app can display my health journal, charts, and trends.",
      status: "Always active — required to use Floline.",
    },
    audioTranscription: {
      label: "Audio transcription",
      text: "I consent to my voice recordings being sent to a speech-to-text service to transcribe check-in entries. Recordings are deleted after transcription.",
      withdrawWarning:
        "Withdrawing this consent will disable voice-based check-ins.",
    },
    aiExtraction: {
      label: "AI-assisted data extraction",
      text: "I consent to my check-in text being sent to an AI model to extract structured pain and symptom data. No data is used for model training.",
      withdrawWarning:
        "Withdrawing this consent will disable automatic extraction of structured data from your entries.",
    },
    aiInsights: {
      label: "AI-generated insights",
      text: "I consent to my aggregated health data being analysed by an AI model to generate personalised trends, summaries, and suggestions.",
      withdrawWarning:
        "Withdrawing this consent will disable AI-generated summaries and suggestions.",
    },
  },
  saveSettings: "Save settings",
  saved: "Settings saved.",
  saveFailed: "Failed to save settings. Please try again.",
} as const;

export const settingsFi = {
  title: "Asetukset",
  profile: {
    title: "Profiili",
    description: "Päivitä nimesi ja kirjautumissähköposti.",
    firstName: "Etunimi",
    lastName: "Sukunimi",
    email: "Sähköposti",
    currentEmail: "Nykyisen sähköpostin vahvistus",
    currentEmailHint: "Vaaditaan vain, jos vaihdat sähköpostia.",
    currentEmailMismatch: "Nykyinen sähköposti ei vastaa tilin sähköpostia.",
    emailInUse: "Sähköposti on jo käytössä.",
  },
  unsavedDialog: {
    title: "Tallentamattomia muutoksia",
    description: "Sinulla on tallentamattomia asetuksia. Tallennetaanko ennen poistumista?",
    saveAndLeave: "Tallenna ja poistu",
    discardAndLeave: "Hylkää ja poistu",
    stay: "Jää",
  },
  bodyDiagram: {
    title: "Kehokaavio",
    description: "Valitse kirjausnäkymään sopiva paperinukketyyli.",
    selectionLabel: "Paperinuken valinta",
    placeholder: "Valitse kehomalli",
    masculine: "Maskuliininen",
    feminine: "Feminiininen",
  },
  language: {
    title: "Kieli",
    description: "Valitse sovelluksen käyttöliittymän kieli.",
    label: "Sovelluksen kieli",
    placeholder: "Valitse kieli",
    english: "English",
    finnish: "Suomi",
  },
  aiCheckInLanguage: {
    title: "AI-kirjauksen kieli",
    description: "Valitse AI-kirjauksen äänilitteroinnissa käytettävä kieli.",
    label: "AI-kirjauksen litterointikieli",
    placeholder: "Valitse kieli",
    appDefault: "Käytä sovelluksen kieltä",
    english: "English",
    finnish: "Suomi",
  },
  dateFormat: {
    title: "Päivämäärän muoto",
    description: "Valitse, miten päivämäärät näytetään koko sovelluksessa.",
    label: "Päivämäärän esitystapa",
    placeholder: "Valitse muoto",
    locale: "Käytä kielen oletusta",
    dmy: "PP/KK/VVVV",
    mdy: "KK/PP/VVVV",
    ymd: "VVVV-KK-PP",
  },
  weather: {
    title: "Sää",
    description: "Lisää oletussijainti nähdäksesi säätiedot kipukäyrien päällä.",
    locationLabel: "Oletussijainti",
    locationPlaceholder: "Kirjoita kaupungin nimi...",
    searchButton: "Hae",
    searchHint: "Valitse kaupunki ehdotuslistasta.",
    searching: "Haetaan kaupunkeja...",
    noMatches: "Hakua vastaavaa kaupunkia ei löytynyt.",
    selectionRequired: "Valitse kaupunki ehdotuslistasta ennen lisäämistä.",
    noLocations: "Sijainteja ei ole vielä lisätty. Hae kaupunkia yllä.",
    defaultBadge: "Oletus",
    setDefault: "Aseta oletukseksi",
    deleteLocation: "Poista",
    deleteConfirmation: "Poista tämä sijainti? Säätiedot säilytetään.",
    deleteConfirmTitle: "Poista sijainti",
    temperatureUnitLabel: "Lämpötilayksikkö",
    celsius: "Celsius (°C)",
    fahrenheit: "Fahrenheit (°F)",
    overlayToggle: "Näytä sää kipukaaviossa",
    privacyNote: "Tallennamme valitsemasi kaupungin ja sen koordinaatit sää- ja aikavyöhykekontekstia varten. Emme seuraa laitteesi GPS-sijaintia tai liikkumista.",
    searchError: "Kaupunkia ei löytynyt. Kokeile toista hakua.",
    rateLimitNote: "Liian monta pyyntöä — odota hetki.",
  },
  consent: {
    title: "Tietojen käsittely ja suostumukset",
    description:
      "Hallitse suostumuksia, jotka ohjaavat miten Floline käsittelee terveystietojasi.",
    saveError: "Suostumuksen tallentaminen epäonnistui. Yritä uudelleen.",
    saved: "Suostumus päivitetty.",
    granted: "Myönnetty",
    withdrawn: "Peruutettu",
    grantAction: "Anna suostumus",
    withdrawConfirmTitle: "Peruuta suostumus",
    withdrawConfirmDescription:
      "Haluatko varmasti peruuttaa suostumuksen: {{label}}? Siitä riippuvat toiminnot poistetaan käytöstä.",
    withdrawConfirmAction: "Peruuta suostumus",
    showHistory: "Näytä suostumushistoria",
    hideHistory: "Piilota suostumushistoria",
    historyTitle: "Suostumushistoria",
    historyEmpty: "Suostumustapahtumia ei ole vielä kirjattu.",
    historyVersion: "v{{version}}",
    historyAction_give: "Myönnetty",
    historyAction_withdraw: "Peruutettu",
    coreHealthProcessing: {
      label: "Terveystietojen peruskäsittely",
      text: "Annan suostumukseni siihen, että Floline tallentaa ja käsittelee syöttämäni kipu-, oire- ja hyvinvointitiedot, jotta sovellus voi näyttää terveyspäiväkirjani, kaaviot ja trendit.",
      status: "Aina aktiivinen — vaaditaan Flolinen käyttöön.",
    },
    audioTranscription: {
      label: "Äänen litterointi",
      text: "Annan suostumukseni siihen, että äänitallenteeni lähetetään puheentunnistuspalveluun kirjausten litteroimiseksi. Tallenteet poistetaan litteroinnin jälkeen.",
      withdrawWarning:
        "Tämän suostumuksen peruuttaminen poistaa äänipohjaiset kirjaukset käytöstä.",
    },
    aiExtraction: {
      label: "Tekoälypohjainen tietojen poiminta",
      text: "Annan suostumukseni siihen, että kirjaustekstini lähetetään tekoälymallille jäsennellyn kipu- ja oiretiedon poimimiseksi. Tietoja ei käytetä mallin kouluttamiseen.",
      withdrawWarning:
        "Tämän suostumuksen peruuttaminen poistaa automaattisen jäsennellyn tiedon poiminnan käytöstä.",
    },
    aiInsights: {
      label: "Tekoälyn tuottamat oivallukset",
      text: "Annan suostumukseni siihen, että koostettua terveystietoani analysoidaan tekoälymallilla henkilökohtaisten trendien, yhteenvetojen ja ehdotusten tuottamiseksi.",
      withdrawWarning:
        "Tämän suostumuksen peruuttaminen poistaa tekoälyn tuottamat yhteenvedot ja ehdotukset käytöstä.",
    },
  },
  saveSettings: "Tallenna asetukset",
  saved: "Asetukset tallennettu.",
  saveFailed: "Asetusten tallennus epäonnistui. Yritä uudelleen.",
} as const;
